import { useState, useCallback, useEffect } from 'react';
import type { PathAnchor, PathElement, CanvasElement } from '../../types';
import { recalcPathElement } from '../../lib/pathUtils';
import { useStudioStore } from '../../stores/studioStore';

interface PathPointEditorProps {
  element: PathElement;
  artboardLeft: number;
  artboardTop: number;
  zoom: number;
  panX: number;
  panY: number;
  onExitEdit: () => void;
}

export default function PathPointEditor({
  element,
  artboardLeft,
  artboardTop,
  zoom,
  panX,
  panY,
  onExitEdit,
}: PathPointEditorProps) {
  const [dragging, setDragging] = useState<{
    type: 'anchor' | 'handleIn' | 'handleOut';
    index: number;
  } | null>(null);

  const toScreen = useCallback(
    (ax: number, ay: number) => ({
      sx: (artboardLeft + element.x + ax) * zoom + panX,
      sy: (artboardTop + element.y + ay) * zoom + panY,
    }),
    [artboardLeft, artboardTop, element.x, element.y, zoom, panX, panY]
  );

  const fromScreen = useCallback(
    (sx: number, sy: number) => ({
      ax: (sx - panX) / zoom - artboardLeft - element.x,
      ay: (sy - panY) / zoom - artboardTop - element.y,
    }),
    [artboardLeft, artboardTop, element.x, element.y, zoom, panX, panY]
  );

  const commitAnchors = useCallback(
    (newAnchors: PathAnchor[]) => {
      const result = recalcPathElement(newAnchors, element.x, element.y, element.closed);
      useStudioStore.getState().updateElement(element.id, {
        anchors: result.anchors,
        pathData: result.pathData,
        x: result.x + element.x,
        y: result.y + element.y,
        width: result.width,
        height: result.height,
      } as Partial<CanvasElement>);
    },
    [element.id, element.x, element.y, element.closed]
  );

  const handlePointerDown = useCallback(
    (type: 'anchor' | 'handleIn' | 'handleOut', index: number, e: React.PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      setDragging({ type, index });
    },
    []
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging) return;
      const { ax, ay } = fromScreen(e.clientX, e.clientY);
      const anchors = element.anchors.map((a) => ({
        ...a,
        handleIn: a.handleIn ? { ...a.handleIn } : undefined,
        handleOut: a.handleOut ? { ...a.handleOut } : undefined,
      }));
      const target = anchors[dragging.index];
      if (!target) return;

      if (dragging.type === 'anchor') {
        const dx = ax - target.x;
        const dy = ay - target.y;
        target.x = ax;
        target.y = ay;
        if (target.handleIn) {
          target.handleIn.x += dx;
          target.handleIn.y += dy;
        }
        if (target.handleOut) {
          target.handleOut.x += dx;
          target.handleOut.y += dy;
        }
      } else if (dragging.type === 'handleIn') {
        target.handleIn = { x: ax, y: ay };
      } else if (dragging.type === 'handleOut') {
        target.handleOut = { x: ax, y: ay };
      }

      commitAnchors(anchors);
    },
    [dragging, fromScreen, element.anchors, commitAnchors]
  );

  const handlePointerUp = useCallback(() => {
    setDragging(null);
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onExitEdit();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onExitEdit]);

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      style={{ width: '100%', height: '100%', zIndex: 50 }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
    >
      {element.anchors.map((anchor, i) => {
        const { sx, sy } = toScreen(anchor.x, anchor.y);

        return (
          <g key={i}>
            {anchor.handleIn && (
              <>
                <line
                  x1={sx}
                  y1={sy}
                  x2={toScreen(anchor.handleIn.x, anchor.handleIn.y).sx}
                  y2={toScreen(anchor.handleIn.x, anchor.handleIn.y).sy}
                  stroke="#06b6d4"
                  strokeWidth={1}
                  className="pointer-events-none"
                />
                <circle
                  cx={toScreen(anchor.handleIn.x, anchor.handleIn.y).sx}
                  cy={toScreen(anchor.handleIn.x, anchor.handleIn.y).sy}
                  r={4}
                  fill="#06b6d4"
                  stroke="#ffffff"
                  strokeWidth={1}
                  className="pointer-events-auto cursor-move"
                  onPointerDown={(e) => handlePointerDown('handleIn', i, e)}
                />
              </>
            )}
            {anchor.handleOut && (
              <>
                <line
                  x1={sx}
                  y1={sy}
                  x2={toScreen(anchor.handleOut.x, anchor.handleOut.y).sx}
                  y2={toScreen(anchor.handleOut.x, anchor.handleOut.y).sy}
                  stroke="#06b6d4"
                  strokeWidth={1}
                  className="pointer-events-none"
                />
                <circle
                  cx={toScreen(anchor.handleOut.x, anchor.handleOut.y).sx}
                  cy={toScreen(anchor.handleOut.x, anchor.handleOut.y).sy}
                  r={4}
                  fill="#06b6d4"
                  stroke="#ffffff"
                  strokeWidth={1}
                  className="pointer-events-auto cursor-move"
                  onPointerDown={(e) => handlePointerDown('handleOut', i, e)}
                />
              </>
            )}
            <rect
              x={sx - 5}
              y={sy - 5}
              width={10}
              height={10}
              fill="#ffffff"
              stroke="#06b6d4"
              strokeWidth={1.5}
              className="pointer-events-auto cursor-move"
              onPointerDown={(e) => handlePointerDown('anchor', i, e)}
            />
          </g>
        );
      })}
    </svg>
  );
}
